/**
 * Iter 25 (D3) — Landing "Watch the demo" block.
 *
 * The user wanted a 60-second screen recording of the Content Engine
 * generating a grounded welcome email with $29/FOUNDER19 specifics.
 * Capturing a real recording is out of scope for the coding agent
 * (no screen-record capability), so we ship Option B: a CSS-animated
 * stylized mockup of the same flow.
 *
 * The mockup auto-loops every ~14 seconds:
 *   1. Renders the Content Engine "Purpose" input being typed.
 *   2. Highlights the Generate button briefly.
 *   3. Streams the resulting email into the output panel, with the
 *      $29 / FOUNDER19 / no-contracts facts highlighted as they appear.
 *
 * The looping animation runs in the poster frame at all times so the
 * visitor sees motion the instant they scroll into view. The Play
 * button + caption underneath links to /signup so the demo doubles
 * as a conversion lever.
 *
 * Replace with a real MP4 once recorded — drop the asset at
 * /public/demo/welcome-email-demo.mp4 and swap the <Mockup /> for
 * <video>.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, FileText, Play, Sparkles } from "lucide-react";

// Demo "script" — what gets typed in the mock UI on each loop.
const PROMPT = "Write a welcome email for new customers";
const EMAIL = (
  "# Welcome to Symplee!\n\n" +
  "Hi {{first_name}},\n\n" +
  "Thanks for trying Symplee — you're on our $29/month plan, " +
  "or just $19/month for life with promo FOUNDER19.\n\n" +
  "No contracts, cancel anytime. If anything feels off, real humans " +
  "reply to support@symplee.ai."
);
// Markers to syntax-highlight as the email streams.
const HIGHLIGHTS = ["$29/month", "$19/month", "FOUNDER19", "No contracts", "support@symplee.ai"];

const TICK_MS = 25;
const PROMPT_PAUSE_MS = 600;
const GENERATE_PAUSE_MS = 700;
const LOOP_PAUSE_MS = 2500;

export function LandingDemoVideo() {
  // Phase state machine: "typing-prompt" → "generating" → "streaming-email" → "rest"
  const [phase, setPhase] = useState("typing-prompt");
  const [promptText, setPromptText] = useState("");
  const [emailText, setEmailText] = useState("");

  useEffect(() => {
    let stopped = false;
    let timeoutId = null;

    const after = (ms, fn) => { timeoutId = setTimeout(fn, ms); };

    const typePrompt = (i = 0) => {
      if (stopped) return;
      if (i > PROMPT.length) {
        setPhase("generating");
        after(GENERATE_PAUSE_MS, streamEmail);
        return;
      }
      setPromptText(PROMPT.slice(0, i));
      after(TICK_MS, () => typePrompt(i + 1));
    };

    const streamEmail = (i = 0) => {
      if (stopped) return;
      setPhase("streaming-email");
      if (i > EMAIL.length) {
        setPhase("rest");
        after(LOOP_PAUSE_MS, () => {
          setPromptText(""); setEmailText("");
          after(PROMPT_PAUSE_MS, () => typePrompt(0));
        });
        return;
      }
      setEmailText(EMAIL.slice(0, i));
      after(TICK_MS, () => streamEmail(i + 1));
    };

    after(PROMPT_PAUSE_MS, typePrompt);
    return () => { stopped = true; if (timeoutId) clearTimeout(timeoutId); };
  }, []);

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="mx-auto mt-16 max-w-5xl px-6 sm:mt-24"
      data-testid="landing-demo-section"
    >
      <div className="text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300">
          <Sparkles className="h-3 w-3" /> See it work
        </span>
        <h2
          className="font-display mt-4 text-3xl font-light leading-tight tracking-tight sm:text-4xl"
          data-testid="landing-demo-headline"
        >
          Symplee writes a customer email — grounded in <span className="brand-text">your business data</span> — in 8 seconds.
        </h2>
        <p
          className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-white/65"
          data-testid="landing-demo-caption"
        >
          No mockup. The output references your real pricing, your real promises, your real voice.
          Try it on the live app — it takes 30 seconds.
        </p>
      </div>

      <div className="relative mt-8 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] shadow-2xl" data-testid="landing-demo-frame">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 border-b border-white/5 px-4 py-2.5">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          </div>
          <div className="ml-3 flex-1 text-center text-[11px] text-slate-500 dark:text-white/40">
            app.symplee.com / Content Engine
          </div>
        </div>

        {/* Body — left "input" panel + right "output" panel */}
        <div className="grid gap-0 sm:grid-cols-2">
          {/* Input panel */}
          <div className="space-y-4 border-b border-white/5 p-6 sm:border-b-0 sm:border-r">
            <div className="flex items-center gap-2 text-xs text-fuchsia-300">
              <FileText className="h-3.5 w-3.5" />
              <span className="font-medium uppercase tracking-wider">Email generator</span>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-white/45">
                Purpose
              </div>
              <div
                className="mt-1.5 min-h-[60px] rounded-xl border border-white/10 bg-black/30 p-3 font-mono text-sm text-slate-900 dark:text-white"
                data-testid="landing-demo-prompt"
              >
                {promptText}
                {phase === "typing-prompt" && (
                  <span className="ml-0.5 inline-block h-3 w-[2px] animate-pulse bg-fuchsia-300 align-middle" aria-hidden />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/[0.06] px-3 py-2 text-xs text-cyan-200">
              <BookOpen className="h-3.5 w-3.5" />
              Grounded in your KB · 4 docs
            </div>
            <button
              type="button"
              className={`w-full rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                phase === "generating"
                  ? "scale-[0.98] bg-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/40 ring-2 ring-fuchsia-300/50"
                  : "bg-white text-black hover:bg-zinc-200"
              }`}
              aria-disabled
              tabIndex={-1}
            >
              {phase === "generating" ? "Generating…" : "Generate"}
            </button>
          </div>

          {/* Output panel */}
          <div className="p-6">
            <div className="flex items-center gap-2 text-xs text-emerald-300">
              <Sparkles className="h-3.5 w-3.5" />
              <span className="font-medium uppercase tracking-wider">Output</span>
            </div>
            <div
              className="mt-3 min-h-[260px] whitespace-pre-wrap rounded-xl border border-white/10 bg-black/30 p-4 text-sm leading-relaxed text-slate-900 dark:text-white/90"
              data-testid="landing-demo-output"
            >
              <HighlightedText text={emailText} markers={HIGHLIGHTS} />
              {phase === "streaming-email" && (
                <span className="ml-0.5 inline-block h-3 w-[2px] animate-pulse bg-emerald-300 align-middle" aria-hidden />
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5" aria-hidden>
              <span className="rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[10px] text-white/65">
                Pricing & Promises.md
              </span>
              <span className="rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[10px] text-white/65">
                Brand Voice.md
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center">
        <Link
          to="/signup?promo=FOUNDER19"
          data-testid="landing-demo-cta"
          className="group inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700"
        >
          <Play className="h-4 w-4" />
          Try it free for 7 days
          <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </Link>
      </div>
    </motion.section>
  );
}

/**
 * Highlights the marker strings within the typed text as they appear,
 * so the visitor's eye is drawn to the brand facts (the moat) the moment
 * they stream in.
 */
function HighlightedText({ text, markers }) {
  // Build a regex that matches any marker; case-insensitive but anchored.
  const pattern = new RegExp(`(${markers.map(escapeRegex).join("|")})`, "g");
  const parts = text.split(pattern);
  return parts.map((part, i) => {
    const isMarker = markers.some((m) => m.toLowerCase() === part.toLowerCase());
    if (!isMarker) return <span key={i}>{part}</span>;
    return (
      <mark
        key={i}
        className="rounded bg-emerald-500/20 px-1 text-emerald-100"
      >
        {part}
      </mark>
    );
  });
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
